import { z } from "zod";
import { faceCollageSchema } from "./face-collage.js";
import { transitionSchema } from "./transition.js";
import { previewUrlSchema, previewUrlWithSizeSchema } from "./preview-url.js";
import { fontFamilySchema, fontWeightSchema, fontLetterSpacingSchema, textTransformSchema, textAlignSchema, } from "./theme-style.js";
import { popHeartsBackgroundImageSchema } from "./background.js";
const insetBoxBackgroundSchema = z.object({
    variant: z.literal("insetBox"),
    padding: z.string(),
    entranceVariant: z.enum(["vertical-grow", "diamonds"]).optional(),
    entranceFrom: z.number().optional(),
    borderRadius: z.string().optional(),
    /**
     * When an array is passed, it cycles through each color in the array for each scene.
     * Using a string applies a static color to every scene.
     */
    boxColor: z.union([z.string(), z.string().array()]).optional(),
    opacity: z.number().optional(),
});
const lottieResourceSchema = z.object({
    url: z.string(),
    endFrameStillUrl: z.string().optional(),
});
const lottieOverlaySchema = z.object({
    variant: z.literal("lottie"),
    urls: lottieResourceSchema.array().optional(),
    aspectRatios: z
        .object({
        portrait: lottieResourceSchema,
        landscape: lottieResourceSchema,
        square: lottieResourceSchema,
    })
        .optional(),
    loop: z.boolean().optional(),
});
const sparklesOverlaySchema = z.object({
    variant: z.literal("sparkles"),
    urls: z.string().array(),
});
const buntingOverlaySchema = z.object({
    variant: z.literal("bunting"),
    buntingUrls: previewUrlSchema.array(),
    starsUrls: previewUrlSchema.array().optional(),
});
const coloredMaskOverlaySchema = z.object({
    variant: z.literal("coloredMask"),
    colors: z.string().array(),
    maskUrl: previewUrlSchema,
});
const candlesMaskOverlaySchema = z.object({
    variant: z.literal("candles"),
    candleUrls: previewUrlSchema.array(),
    wandUrls: previewUrlSchema.array(),
});
const plainColorBackgroundSchema = z.object({
    variant: z.literal("plain-color"),
    // anything that can go in `backgroundColor` style prop
    color: z.string(),
    // for flat colors, blur won't change the color but it will soften the edges,
    // so a transition might look less harsh
    blur: z.number().optional(),
});
const sceneBackgroundSchema = z.discriminatedUnion("variant", [
    insetBoxBackgroundSchema,
    lottieOverlaySchema,
    sparklesOverlaySchema,
    buntingOverlaySchema,
    coloredMaskOverlaySchema,
    candlesMaskOverlaySchema,
    plainColorBackgroundSchema,
]);
const sceneCommonSchema = z.object({
    showLogo: z.boolean().optional(),
    exitTransition: transitionSchema.optional(),
    sceneBackground: sceneBackgroundSchema.optional(),
});
const messageAssetSchema = z.object({
    style: z.record(z.string(), z.any()),
    url: previewUrlWithSizeSchema,
    rotateNudgeOptions: z
        .object({
        min: z.number(),
        max: z.number(),
    })
        .optional(),
});
const messageAssetsSchema = z.array(messageAssetSchema);
const greetingSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("greeting"),
    variant: z.enum(["intro", "outro"]),
    secondsPerCharacter: z.number(),
    minimumMessageDisplayTimeSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    betweenMessageTransition: z
        .object({
        variant: z.enum(["opacity", "scale"]),
        enterDurationSeconds: z.number(),
        exitDurationSeconds: z.number(),
    })
        .optional(),
    messageAssets: z.array(messageAssetsSchema).min(1).max(3).optional(),
});
const lottieSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("lottie"),
    lottieUrl: z.string(),
    endFrameStillUrl: z.string().optional(),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
});
const birthdayCakeSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("birthdayCakeIntro"),
    lottieUrl: z.string(),
    durationSeconds: z.number(),
    title: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
    }),
    sparklesUrls: z.tuple([z.string(), z.string(), z.string()]),
    faceCollage: faceCollageSchema.optional(),
});
const birthdayCandlesSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("birthdayCandlesIntro"),
    durationSeconds: z.number(),
    candleUrls: z.array(previewUrlSchema),
    wandUrls: z.array(previewUrlSchema),
    faceCollage: faceCollageSchema.optional(),
});
const inkyGuestBookIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("inkyGuestBookIntro"),
    videoUrl: previewUrlSchema,
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    variant: z.enum(["intro", "outro"]),
    title: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
    }),
    subtitle: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
    }),
});
const generalSolidIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("generalSolidIntro"),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    transitionVariant: z
        .enum([
        "vertical-scale",
        "vertical-mask",
        "symmetry-box",
        "pop-scale",
        "type-on",
    ])
        .optional(),
    title: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        letterSpacing: fontLetterSpacingSchema,
    }),
});
const bigCongratsBurstImageAnimateSchema = z.discriminatedUnion("variant", [
    z.object({
        variant: z.literal("none"),
    }),
    z.object({
        // random rotation
        variant: z.literal("rotate"),
    }),
    z.object({
        // slowly tilt in a direction
        variant: z.literal("tilt"),
        direction: z.enum(["left", "right"]),
    }),
]);
const bigCongratsBurstImageLocationZonesSchema = [
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
];
export const bigCongratsBurstImageLocationZones = bigCongratsBurstImageLocationZonesSchema.map((x) => x.value);
const bigCongratsBurstImageSchema = z.object({
    url: previewUrlSchema,
    // the width and height of the full-sized image
    width: z.number(),
    height: z.number(),
    delaySeconds: z.number().optional(),
    // where this image will end up
    location: z.object({
        edge: z.enum(["top", "bottom", "left", "right"]),
        // The edge is split into 10 zones. If "top" or "bottom", the zones are left
        // to right. If "left" or "right", the zones are top to bottom.
        zone: z.union(bigCongratsBurstImageLocationZonesSchema),
    }),
    scale: z.number().optional(),
    rotate: z.number().optional(),
    animate: bigCongratsBurstImageAnimateSchema.optional(),
});
const bigCongratsIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("bigCongratsIntro"),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    burstImages: bigCongratsBurstImageSchema.array(),
    bigCongratsText: z.object({
        // invert the color of the image
        invert: z.boolean().optional(),
        maxScale: z.number().optional(),
        url: previewUrlSchema,
    }),
});
const schoolSpiritIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("schoolSpiritIntro"),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    bannerImage: z.object({
        scale: z.number().optional(),
        url: previewUrlWithSizeSchema,
    }),
});
const meadowFlowersIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("meadowFlowersIntro"),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    title: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
        textTransform: textTransformSchema.optional(),
    }),
    subtitle: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
        textTransform: textTransformSchema.optional(),
    }),
});
const mountainViewIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("mountainViewIntro"),
    durationSeconds: z.number(),
    secondsPerCharacter: z.number(),
    faceCollage: faceCollageSchema.optional(),
    title: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
        textTransform: textTransformSchema.optional(),
        leading: z.number().optional(),
        textAlign: textAlignSchema,
        backgroundColor: z.string().optional(),
    }),
    subtitle: z.object({
        fontFamily: fontFamilySchema,
        fontSize: z.number(),
        fontWeight: fontWeightSchema,
        textColor: z.string(),
        textTransform: textTransformSchema.optional(),
        leading: z.number().optional(),
        textAlign: textAlignSchema,
        backgroundColor: z.string().optional(),
    }),
});
const paintDaubsIntroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("paintDaubsIntro"),
    durationSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    mask1: previewUrlSchema,
    mask2: previewUrlSchema,
});
const typeOnGreetingSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("typeOnGreeting"),
    variant: z.enum(["intro", "outro"]),
    secondsPerCharacter: z.number(),
    minimumMessageDisplayTimeSeconds: z.number(),
    // A value between 0 and 1 that represents the maxWidth of the container
    widthFactor: z.number().optional(),
    // assets shared by all messages (are displayed for all messages)
    sharedMessageAssets: messageAssetsSchema.optional(),
    messageAssets: z.array(messageAssetsSchema).min(1).max(3).optional(),
});
const greetingLinesAsScenes = sceneCommonSchema.extend({
    type: z.literal("greetingLinesAsScenes"),
    variant: z.enum(["intro", "outro"]),
    secondsPerCharacter: z.number(),
    minimumMessageDisplayTimeSeconds: z.number(),
    faceCollage: faceCollageSchema.optional(),
    messageAssets: z.array(messageAssetsSchema).min(1).max(3).optional(),
});
const outroColorVariant = z.enum(["darkBlue", "lightBlue", "lightOrange"]);
const outroBackgroundSchema = z.discriminatedUnion("variant", [
    z.object({
        variant: z.literal("none"),
    }),
    z.object({
        variant: z.literal("slanted-catchup"),
        backgroundColor: z.string(),
        durationSeconds: z.number(),
        delaySeconds: z.number().optional(),
    }),
]);
const outroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("outro"),
    colorVariant: outroColorVariant.optional(),
    entranceVariant: z.enum(["slide-in-fade-in", "scale-in"]).optional(),
    background: outroBackgroundSchema.optional(),
});
const popHeartsOutroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("popHeartsOutro"),
    popOutStartSeconds: z.number(),
    animationDurationSeconds: z.number(),
    hearts: popHeartsBackgroundImageSchema.array(),
});
const lottieOutroSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("lottieOutro"),
    lottieUrl: z.string(),
    endFrameStillUrl: z.string().optional(),
    durationSeconds: z.number(),
    lottiePosition: z
        .enum(["left", "right", "top", "bottom", "center"])
        .optional(),
    logo: z
        .object({
        transition: z.enum(["scale", "slide-down"]).optional(),
        position: z.enum(["left", "right", "top", "bottom", "center"]).optional(),
    })
        .optional(),
});
const clipSeriesBasicSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("clip-series-basic"),
});
const circleGreetingSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("circleGreeting"),
    variant: z.enum(["intro", "outro"]),
    sparklesUrls: z.array(z.string()),
    colors: z.array(z.string()),
});
const textTransitionSchema = z.object({
    text: z.string(),
    fontFamily: fontFamilySchema,
    fontSize: z.number(),
    fontWeight: fontWeightSchema,
    letterSpacing: fontLetterSpacingSchema,
    textTransform: textTransformSchema.optional(),
    textColor: z.string(),
    transition: z
        .enum(["drop-character", "fade-character", "fade-up", "squish-in"])
        .optional(),
});
const textTransitionSceneConfigImageSchema = z.object({
    url: previewUrlSchema,
    transformations: z.enum(["flip-x"]).array().optional(),
});
const textTransitionSceneConfigSchema = sceneCommonSchema.extend({
    type: z.literal("textTransition"),
    variant: z.enum(["intro", "outro"]),
    durationSeconds: z.number(),
    title: textTransitionSchema,
    subtitle1: textTransitionSchema.optional(),
    subtitle2: textTransitionSchema.optional(),
    faceCollage: faceCollageSchema.optional(),
    leftImage: textTransitionSceneConfigImageSchema.optional(),
    rightImage: textTransitionSceneConfigImageSchema.optional(),
    outroVariant: z.enum(["darkBlue", "lightBlue"]).optional(),
});
const imageSequenceConfigSchema = sceneCommonSchema.extend({
    type: z.literal("imageSequence"),
    urlPrefix: z.string(),
    maskPrefix: z.string().optional(),
    frameCount: z.number(),
    zeroPad: z.number().optional(),
});
// This type is too complex to infer, so we define it manually below
// to avoid "The inferred type of this node exceeds the maximum length
// the compiler will serialize. An explicit type annotation is needed." errors
export const sceneConfigSchema = z
    .discriminatedUnion("type", [
    greetingSceneConfigSchema,
    typeOnGreetingSceneConfigSchema,
    greetingLinesAsScenes,
    outroSceneConfigSchema,
    lottieOutroSceneConfigSchema,
    clipSeriesBasicSceneConfigSchema,
    lottieSceneConfigSchema,
    imageSequenceConfigSchema,
    inkyGuestBookIntroSceneConfigSchema,
    birthdayCakeSceneConfigSchema,
    circleGreetingSceneConfigSchema,
    textTransitionSceneConfigSchema,
    generalSolidIntroSceneConfigSchema,
    bigCongratsIntroSceneConfigSchema,
    schoolSpiritIntroSceneConfigSchema,
    meadowFlowersIntroSceneConfigSchema,
    mountainViewIntroSceneConfigSchema,
    popHeartsOutroSceneConfigSchema,
    paintDaubsIntroSceneConfigSchema,
    birthdayCandlesSceneConfigSchema,
])
    .superRefine((data, ctx) => {
    if (data.type === "schoolSpiritIntro") {
        // just to give time for transitions, etc
        if (data.durationSeconds < 4) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "durationSeconds must be at least 4 seconds long.",
                path: ["durationSeconds"],
            });
        }
    }
});
export const greetingSceneTypes = [
    "greeting",
    "typeOnGreeting",
    "greetingLinesAsScenes",
    "circleGreeting",
];
export function getIsGreetingScene(scene) {
    return greetingSceneTypes.includes(scene.type);
}
